import React, { useState } from 'react';

interface WindowsInstallationGuideProps {
  lang: string;
}

const WindowsInstallationGuide: React.FC<WindowsInstallationGuideProps> = ({ lang }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [showDefenderGuide, setShowDefenderGuide] = useState(false);

  const installationSteps = [
    {
      id: 1,
      image: '/img/aivuebiz_window_install_1.png',
      title: lang === 'ko' ? '추가 작업 선택' : 'Select Additional Tasks',
      description: lang === 'ko'
        ? '설치 중 수행할 추가 작업을 선택합니다.'
        : 'Select additional tasks to perform during installation.',
      details: lang === 'ko'
        ? ['바탕화면 바로가기 생성 옵션을 체크하면 편리합니다', 'Next 버튼을 클릭하여 다음 단계로 진행합니다']
        : ['Check "Create a desktop shortcut" for easy access', 'Click Next to proceed to the next step'],
      tips: lang === 'ko'
        ? '바탕화면 바로가기를 생성하면 앱을 쉽게 실행할 수 있습니다.'
        : 'Creating a desktop shortcut allows you to easily launch the app.'
    },
    {
      id: 2,
      image: '/img/aivuebiz_window_install_2.png',
      title: lang === 'ko' ? '설치 준비 완료' : 'Ready to Install',
      description: lang === 'ko'
        ? '설치를 시작할 준비가 완료되었습니다.'
        : 'Setup is now ready to begin installing AivueBiz Desktop.',
      details: lang === 'ko'
        ? ['설치 경로와 설정을 확인합니다', 'Install 버튼을 클릭하여 설치를 시작합니다', '변경이 필요한 경우 Back 버튼을 사용합니다']
        : ['Review the installation path and settings', 'Click Install to begin the installation', 'Use Back button if you need to make changes'],
      tips: lang === 'ko'
        ? '기본 설정으로 설치하는 것을 권장합니다.'
        : 'Installing with default settings is recommended.'
    },
    {
      id: 3,
      image: '/img/aivuebiz_window_install_3.png',
      title: lang === 'ko' ? '설치 완료' : 'Installation Complete',
      description: lang === 'ko'
        ? 'AivueBiz Desktop 설치가 완료되었습니다.'
        : 'Setup has finished installing AivueBiz Desktop.',
      details: lang === 'ko'
        ? ['"Launch AivueBiz Desktop" 옵션을 체크하면 바로 실행됩니다', 'Finish 버튼을 클릭하여 설치를 완료합니다', '바탕화면 아이콘을 통해서도 실행 가능합니다']
        : ['Check "Launch AivueBiz Desktop" to start immediately', 'Click Finish to complete the installation', 'You can also launch from the desktop icon'],
      tips: lang === 'ko'
        ? '설치 완료 후 바로 실행하여 계정 설정을 진행하세요.'
        : 'Launch immediately after installation to set up your account.'
    },
    {
      id: 4,
      image: '/img/aivuebiz_window_install_4.png',
      title: lang === 'ko' ? 'Windows Defender 보안 경고' : 'Windows Defender Security Alert',
      description: lang === 'ko'
        ? 'Windows에서 앱을 처음 실행할 때 보안 경고가 나타날 수 있습니다.'
        : 'Windows may show a security alert when running the app for the first time.',
      details: lang === 'ko'
        ? ['이 경고는 새로운 앱에 대한 Windows의 기본 보호 기능입니다', '"추가 정보"를 클릭한 다음 "실행"을 선택합니다', 'AivueBiz는 안전한 앱이므로 안심하고 실행하세요']
        : ['This warning is Windows\' default protection for new apps', 'Click "More info" then select "Run anyway"', 'AivueBiz is a safe application, you can run it with confidence'],
      tips: lang === 'ko'
        ? '이 경고는 처음 실행할 때만 나타나며, 이후에는 나타나지 않습니다.'
        : 'This warning only appears on first run and won\'t show again.',
      isWarning: true
    }
  ];

  const handleStepClick = (index: number) => {
    setActiveStep(index);
  };

  const handleDefenderGuideToggle = () => {
    setShowDefenderGuide(!showDefenderGuide);
    if (!showDefenderGuide) {
      setActiveStep(3);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 mb-12 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-3">
          {lang === 'ko' ? 'Windows 설치 상세 가이드' : 'Windows Installation Guide'}
        </h2>
        <p className="text-center text-gray-600 max-w-3xl mx-auto">
          {lang === 'ko'
            ? 'AivueBiz Desktop을 Windows에 설치하는 방법을 단계별로 안내합니다.'
            : 'Follow these step-by-step instructions to install AivueBiz Desktop on Windows.'}
        </p>
      </div>

      {/* Windows Defender Alert Banner */}
      <div className="mb-6">
        <button
          onClick={handleDefenderGuideToggle}
          className={`w-full p-4 rounded-xl border-2 transition-all duration-300 ${showDefenderGuide
            ? 'bg-amber-50 border-amber-300 shadow-lg'
            : 'bg-amber-50/50 border-amber-200 hover:border-amber-300 hover:shadow-md'
            }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <span className="text-3xl">🛡️</span>
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">
                  {lang === 'ko'
                    ? 'Windows Defender 경고가 나타나나요?'
                    : 'Seeing Windows Defender Alert?'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {lang === 'ko'
                    ? '걱정하지 마세요! 새 앱에 대한 일반적인 보안 확인입니다. 해결 방법을 확인하세요.'
                    : "Don't worry! It's a standard security check for new apps. See how to proceed."}
                </p>
              </div>
            </div>
            <div className={`transform transition-transform duration-300 ${showDefenderGuide ? 'rotate-180' : ''}`}>
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </button>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          {installationSteps.map((step, index) => (
            <div key={step.id} className="flex-1 relative">
              <button
                onClick={() => handleStepClick(index)}
                className={`w-full group transition-all duration-300 ${activeStep === index ? 'scale-105' : ''
                  }`}
              >
                <div className="flex flex-col items-center">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg
                    transition-all duration-300 shadow-lg
                    ${activeStep === index
                      ? step.isWarning
                        ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white ring-4 ring-amber-200'
                        : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white ring-4 ring-blue-200'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }
                  `}>
                    {step.isWarning ? '!' : step.id}
                  </div>
                  <span className={`
                    mt-2 text-xs font-medium transition-colors duration-300
                    ${activeStep === index ? 'text-blue-600' : 'text-gray-500'}
                  `}>
                    {lang === 'ko' ? `${step.isWarning ? '보안' : `${step.id}단계`}` : `${step.isWarning ? 'Security' : `Step ${step.id}`}`}
                  </span>
                </div>
              </button>
              {index < installationSteps.length - 1 && (
                <div className={`
                  absolute top-6 left-1/2 w-full h-0.5 transition-all duration-500
                  ${index < activeStep ? 'bg-blue-500' : 'bg-gray-300'}
                `} style={{ width: 'calc(100% - 48px)', left: '50%' }} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Active Step Content */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6">
        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Image Section */}
          <div className="order-2 md:order-1">
            <div className="relative group overflow-hidden rounded-xl shadow-2xl bg-white">
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <img
                src={installationSteps[activeStep].image}
                alt={installationSteps[activeStep].title}
                className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden flex-col items-center justify-center p-12 bg-gradient-to-br from-gray-100 to-gray-200">
                <div className="text-6xl mb-4">🖼️</div>
                <p className="text-gray-600 text-center font-medium">
                  {lang === 'ko' ? '설치 화면 이미지' : 'Installation Screen Image'}
                </p>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="order-1 md:order-2">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-3 flex items-center">
                {installationSteps[activeStep].isWarning && (
                  <span className="text-amber-500 mr-2">⚠️</span>
                )}
                {installationSteps[activeStep].title}
              </h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                {installationSteps[activeStep].description}
              </p>
            </div>

            {/* Detailed Steps */}
            <div className="space-y-4 mb-6">
              {installationSteps[activeStep].details.map((detail, idx) => (
                <div key={idx} className="flex items-start group">
                  <div className={`
                    flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 mt-0.5
                    transition-all duration-300 group-hover:scale-110
                    ${installationSteps[activeStep].isWarning
                      ? 'bg-amber-100 text-amber-600'
                      : 'bg-blue-100 text-blue-600'
                    }
                  `}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-700 leading-relaxed flex-1">
                    {detail}
                  </p>
                </div>
              ))}
            </div>

            {/* Tip Box */}
            <div className={`
              p-4 rounded-lg border-l-4 
              ${installationSteps[activeStep].isWarning
                ? 'bg-amber-50 border-amber-400'
                : 'bg-blue-50 border-blue-400'
              }
            `}>
              <div className="flex items-start">
                <span className="text-2xl mr-3">💡</span>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">
                    {lang === 'ko' ? '팁' : 'Tip'}
                  </p>
                  <p className="text-sm text-gray-700">
                    {installationSteps[activeStep].tips}
                  </p>
                </div>
              </div>
            </div>

            {/* Windows Defender Specific Instructions */}
            {installationSteps[activeStep].isWarning && (
              <div className="mt-6 p-6 bg-white rounded-lg border-2 border-amber-200">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <span className="text-amber-500 mr-2">🔓</span>
                  {lang === 'ko' ? 'Windows Defender 경고 해결 방법' : 'How to Handle Windows Defender Alert'}
                </h4>

                <div className="space-y-6">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3">
                      {lang === 'ko' ? '1단계: 경고 메시지 확인' : 'Step 1: Identify the Alert'}
                    </h5>
                    <div className="bg-blue-900 text-white p-4 rounded-lg font-mono text-sm">
                      <div className="mb-2">Windows protected your PC</div>
                      <div className="text-gray-300 text-xs mb-3">
                        Microsoft Defender SmartScreen prevented an unrecognized app from starting.
                      </div>
                      <div className="text-yellow-300">App: AivueBizDesktopSetup_1.0.19.exe</div>
                      <div className="text-gray-400 text-xs mt-2">Publisher: Unknown</div>
                    </div>
                  </div>

                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3">
                      {lang === 'ko' ? '2단계: 추가 정보 클릭' : 'Step 2: Click More Info'}
                    </h5>
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <p className="text-gray-700 mb-3">
                        {lang === 'ko'
                          ? '경고 창 하단의 "More info" 링크를 클릭합니다.'
                          : 'Click the "More info" link at the bottom of the warning window.'}
                      </p>
                      <button className="text-blue-600 underline font-medium hover:text-blue-700">
                        More info
                      </button>
                    </div>
                  </div>

                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3">
                      {lang === 'ko' ? '3단계: 실행 버튼 클릭' : 'Step 3: Click Run Anyway'}
                    </h5>
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <p className="text-gray-700 mb-4">
                        {lang === 'ko'
                          ? '"More info"를 클릭한 후 나타나는 "Run anyway" 버튼을 클릭합니다.'
                          : 'After clicking "More info", click the "Run anyway" button that appears.'}
                      </p>
                      <div className="flex space-x-3">
                        <button className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors">
                          Don't run
                        </button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium">
                          Run anyway
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      <span className="font-semibold">
                        {lang === 'ko' ? '안전 보장: ' : 'Safety Assured: '}
                      </span>
                      {lang === 'ko'
                        ? 'AivueBiz Desktop은 안전한 애플리케이션입니다. 이 경고는 새로운 앱에 대한 Windows의 표준 보호 절차입니다.'
                        : 'AivueBiz Desktop is a safe application. This warning is Windows\' standard protection procedure for new apps.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
          className={`
            px-6 py-3 rounded-lg font-medium transition-all duration-300
            ${activeStep === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
            }
          `}
          disabled={activeStep === 0}
        >
          <span className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {lang === 'ko' ? '이전' : 'Previous'}
          </span>
        </button>

        <div className="flex space-x-2">
          {installationSteps.map((_, index) => (
            <button
              key={index}
              onClick={() => handleStepClick(index)}
              className={`
                w-2 h-2 rounded-full transition-all duration-300
                ${activeStep === index
                  ? 'w-8 bg-blue-600'
                  : 'bg-gray-300 hover:bg-gray-400'
                }
              `}
            />
          ))}
        </div>

        <button
          onClick={() => setActiveStep(Math.min(installationSteps.length - 1, activeStep + 1))}
          className={`
            px-6 py-3 rounded-lg font-medium transition-all duration-300
            ${activeStep === installationSteps.length - 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:shadow-lg hover:scale-105'
            }
          `}
          disabled={activeStep === installationSteps.length - 1}
        >
          <span className="flex items-center">
            {lang === 'ko' ? '다음' : 'Next'}
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </button>
      </div>

      {/* Quick Tips Section */}
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center mb-2">
            <span className="text-2xl mr-2">⚡</span>
            <h4 className="font-semibold text-gray-900">
              {lang === 'ko' ? '빠른 설치' : 'Quick Install'}
            </h4>
          </div>
          <p className="text-sm text-gray-700">
            {lang === 'ko'
              ? '기본 설정으로 설치하면 2-3분 내에 완료됩니다.'
              : 'Installation with default settings takes only 2-3 minutes.'}
          </p>
        </div>

        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center mb-2">
            <span className="text-2xl mr-2">🔄</span>
            <h4 className="font-semibold text-gray-900">
              {lang === 'ko' ? '자동 업데이트' : 'Auto Updates'}
            </h4>
          </div>
          <p className="text-sm text-gray-700">
            {lang === 'ko'
              ? '앱이 자동으로 최신 버전을 확인하고 업데이트합니다.'
              : 'The app automatically checks for and installs updates.'}
          </p>
        </div>

        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center mb-2">
            <span className="text-2xl mr-2">💬</span>
            <h4 className="font-semibold text-gray-900">
              {lang === 'ko' ? '도움말' : 'Support'}
            </h4>
          </div>
          <p className="text-sm text-gray-700">
            {lang === 'ko'
              ? '설치 중 문제가 있나요? support@aivuebiz.com으로 문의하세요.'
              : 'Having issues? Contact us at support@aivuebiz.com'}
          </p>
        </div>
      </div>
    </div >
  );
};

export default WindowsInstallationGuide;